import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";

import { fetchMeApi } from "@/api/auth";
import { authReadyAtom, authSessionAtom, currentUserAtom, permissionsAtom } from "@/atoms/auth";
import { dictionariesAtom, dictReadyAtom, refreshDictionariesAtom } from "@/atoms/dict";
import { getMockLoginResult, isMockAuthToken } from "@/utils/mockAuth";
import { clearToken, getToken } from "@/utils/storage";

/**
 * 应用 bootstrap：登录后会话变更时统一拉取用户、权限、字典等全局数据
 * 后续菜单等接口在此并行追加即可
 */
export const useAppInit = () => {
  const authSession = useAtomValue(authSessionAtom);
  const setUser = useSetAtom(currentUserAtom);
  const setPermissions = useSetAtom(permissionsAtom);
  const setAuthReady = useSetAtom(authReadyAtom);
  const setDict = useSetAtom(dictionariesAtom);
  const setDictReady = useSetAtom(dictReadyAtom);
  const refreshDict = useSetAtom(refreshDictionariesAtom);

  useEffect(() => {
    const bootstrap = async () => {
      setAuthReady(false);
      setDictReady(false);
      const token = getToken();

      /** 未登录：清空全局状态并标记就绪 */
      const resetGuest = () => {
        setUser(null);
        setPermissions([]);
        setDict([]);
      };

      if (!token) {
        resetGuest();
        setAuthReady(true);
        setDictReady(true);
        return;
      }

      /** 拉取用户与权限 */
      const loadAuth = async () => {
        if (isMockAuthToken(token)) {
          const mock = getMockLoginResult("admin@example.com");
          setUser(mock.user);
          setPermissions(mock.permissions);
          return;
        }
        const data = await fetchMeApi();
        setUser(data.data.user);
        setPermissions(data.data.permissions);
      };

      try {
        // 登录后必须调用的接口并行加载；后续菜单等在此追加
        await Promise.all([loadAuth(), refreshDict()]);
      } catch {
        clearToken();
        resetGuest();
        setDictReady(true);
      } finally {
        setAuthReady(true);
      }
    };
    void bootstrap();
  }, [authSession, refreshDict, setAuthReady, setDict, setDictReady, setPermissions, setUser]);
};
